/**
 * Push Notifications Management
 * Handles subscription, unsubscription, and permission requests
 */

// VAPID public key — fetched from backend at runtime
let VAPID_PUBLIC_KEY = '';

async function getVapidKey(): Promise<string> {
  if (VAPID_PUBLIC_KEY) return VAPID_PUBLIC_KEY;
  try {
    const res = await fetch('/api/v1/notifications/vapid-key');
    const data = await res.json() as any;
    if (data?.success && data?.data?.publicKey) {
      VAPID_PUBLIC_KEY = data.data.publicKey;
    }
  } catch (e) {
    console.warn('[PWA] Failed to fetch VAPID key:', e);
  }
  return VAPID_PUBLIC_KEY;
}

export interface PushSubscriptionJSON {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

/**
 * Register Service Worker and set up push notifications
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.warn('[PWA] Service Worker not supported');
    return null;
  }

  try {
    // VitePWA auto-registers the SW, just wait for it
    const registration = await navigator.serviceWorker.ready;
    console.log('[PWA] Service Worker ready:', registration.scope);
    return registration;
  } catch (error) {
    console.error('[PWA] Service Worker not ready:', error);
    return null;
  }
}

/**
 * Request notification permission
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    console.warn('[PWA] Notifications not supported');
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    return 'denied';
  }

  try {
    const permission = await Notification.requestPermission();
    console.log('[PWA] Notification permission:', permission);
    return permission;
  } catch (error) {
    console.error('[PWA] Error requesting notification permission:', error);
    return 'denied';
  }
}

/**
 * Subscribe to push notifications
 */
export async function subscribeToPush(swRegistration: ServiceWorkerRegistration): Promise<string | null> {
  if (!('pushManager' in swRegistration)) {
    console.warn('[PWA] Push Manager not supported');
    return null;
  }

  const vapidKey = await getVapidKey();
  if (!vapidKey) {
    console.warn('[PWA] VAPID public key not available from server');
    return null;
  }

  try {
    // Check if already subscribed
    let subscription = await swRegistration.pushManager.getSubscription();
    if (subscription) {
      console.log('[PWA] Already subscribed to push');
      // Re-send to server in case it was lost
      const subscriptionJson = subscription.toJSON() as PushSubscriptionJSON;
      fetch('/api/v1/notifications/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(subscriptionJson),
      }).catch(() => {});
      return subscription.endpoint;
    }

    // Subscribe with VAPID key from server
    subscription = await swRegistration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });

    console.log('[PWA] Subscribed to push notifications');

    // Send subscription to server
    const subscriptionJson = subscription.toJSON() as PushSubscriptionJSON;
    await fetch('/api/v1/notifications/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(subscriptionJson),
    });

    return subscription.endpoint;
  } catch (error) {
    console.error('[PWA] Error subscribing to push:', error);
    return null;
  }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPush(swRegistration: ServiceWorkerRegistration): Promise<boolean> {
  if (!('pushManager' in swRegistration)) {
    return false;
  }

  try {
    const subscription = await swRegistration.pushManager.getSubscription();
    if (!subscription) {
      console.log('[PWA] Not subscribed to push');
      return false;
    }

    await subscription.unsubscribe();
    console.log('[PWA] Unsubscribed from push notifications');

    // Notify server
    const subscriptionJson = subscription.toJSON() as PushSubscriptionJSON;
    await fetch('/api/v1/notifications/unsubscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(subscriptionJson),
    });

    return true;
  } catch (error) {
    console.error('[PWA] Error unsubscribing from push:', error);
    return false;
  }
}

/**
 * Get current push subscription
 */
export async function getPushSubscription(
  swRegistration: ServiceWorkerRegistration
): Promise<PushSubscriptionJSON | null> {
  if (!('pushManager' in swRegistration)) {
    return null;
  }

  try {
    const subscription = await swRegistration.pushManager.getSubscription();
    if (!subscription) {
      return null;
    }

    return subscription.toJSON() as PushSubscriptionJSON;
  } catch (error) {
    console.error('[PWA] Error getting push subscription:', error);
    return null;
  }
}

/**
 * Check if PWA is installable
 */
export function isInstallable(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window &&
    Notification.permission !== 'denied'
  );
}

/**
 * Check if running as PWA (installed app)
 */
export function isPWAMode(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
}

/**
 * Get install prompt event (for web app install button)
 */
let deferredPrompt: BeforeInstallPromptEvent | null = null;

export function setupInstallPrompt(): void {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    console.log('[PWA] Install prompt ready');
  });
}

export async function promptInstall(): Promise<boolean> {
  if (!deferredPrompt) {
    console.warn('[PWA] Install prompt not available');
    return false;
  }

  try {
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    console.log('[PWA] User choice:', outcome);

    if (outcome === 'accepted') {
      deferredPrompt = null;
      return true;
    }

    return false;
  } catch (error) {
    console.error('[PWA] Error prompting install:', error);
    return false;
  }
}

/**
 * Helper: Convert VAPID key from base64 to Uint8Array
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return new Uint8Array(Array.from(rawData).map((char) => char.charCodeAt(0)));
}

/**
 * Initialize PWA
 */
export async function initializePWA(): Promise<{
  swRegistration: ServiceWorkerRegistration | null;
  notificationPermission: NotificationPermission;
  pushSubscribed: boolean;
}> {
  console.log('[PWA] Initializing...');

  const swRegistration = await registerServiceWorker();

  // NÃO pedir permissão de notificação no boot — só após login
  // Se já foi concedida antes, apenas re-subscreve
  const notificationPermission = ('Notification' in window) ? Notification.permission : 'denied' as NotificationPermission;
  let pushSubscribed = false;

  if (swRegistration && notificationPermission === 'granted') {
    pushSubscribed = (await subscribeToPush(swRegistration)) !== null;
  }

  setupInstallPrompt();

  console.log('[PWA] Initialized:', { swRegistration: !!swRegistration, notificationPermission, pushSubscribed });

  return {
    swRegistration,
    notificationPermission,
    pushSubscribed,
  };
}

/**
 * Inicializar notificações após login — pede permissão e subscreve push
 */
export async function initializeNotificationsAfterLogin(): Promise<void> {
  console.log('[PWA] Solicitando permissão de notificações pós-login...');
  const permission = await requestNotificationPermission();
  if (permission === 'granted') {
    const sw = await registerServiceWorker();
    if (sw) {
      await subscribeToPush(sw);
    }
  }
}
